/**
 * KRA Photo Journal – PhotoGrid
 *
 * Responsive masonry-ish grid of PhotoCards.
 * Uses CSS grid with responsive columns:
 *   - iPhone (≤390px):  2 columns
 *   - iPad mini (≥768): 3 columns
 *   - MacBook (≥1280):  4–5 columns
 *
 * Implements infinite scroll via useInfiniteScroll hook.
 * Entrance animations are staggered per card via Framer Motion.
 */

import React, { useCallback, useState, useMemo, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { PhotoCard } from "./PhotoCard.js";
import { PhotoModal } from "./PhotoModal.js";
import { ImportToDriveModal } from "./ImportToDriveModal.js";
import { BatchImportModal } from "./BatchImportModal.js";
import { ResizeModal } from "./ResizeModal.js";
import { CollectionPicker } from "./CollectionPicker.js";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll.js";
import { patchPhoto, type TagOptions } from "../lib/api.js";
import { shareImages, type ShareItem } from "../lib/share-files.js";

interface PhotoGridProps {
  photos: PhotoRecord[];
  /** All loaded photos (unfiltered) — for TagEditor option suggestions */
  allPhotos?: PhotoRecord[];
  /** Tag field options from Airtable schema */
  tagOptions?: TagOptions;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error: string | null;
  onUpdatePhoto: (id: string, patch: Partial<PhotoRecord>) => void;
  /** Currently logged-in photographer name, used for per-photographer likes */
  currentUser?: string | null;
  /** Whether the current session has admin role (invite-code or legacy). */
  isAdmin?: boolean;
  /** Kiosk / read-only mode — hide like/download action buttons */
  isReadOnly?: boolean;
  /** Called when a tag chip is clicked on a card — bubble up for filtering */
  onTagClick?: (groupKey: string, tagValue: string) => void;
  /** Deep-link: auto-open modal for this record ID (from ?record= URL param) */
  initialRecordId?: string | null;
  /** Called after initialRecordId has been consumed */
  onInitialRecordConsumed?: () => void;
}

export function PhotoGrid({
  photos,
  allPhotos,
  tagOptions,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  error,
  onUpdatePhoto,
  currentUser,
  isAdmin,
  isReadOnly,
  onTagClick,
  initialRecordId,
  onInitialRecordConsumed,
}: PhotoGridProps) {
  // Store ID only so modal always reflects the latest optimistic state
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [importDrivePhotoId, setImportDrivePhotoId] = useState<string | null>(null);
  // Whether the open import modal is in ⚡ quick mode (skip destination picker).
  const [importDriveQuick, setImportDriveQuick] = useState(false);
  const [resizePhotoId, setResizePhotoId] = useState<string | null>(null);
  const [collectionPhotoId, setCollectionPhotoId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Multi-select mode. Two flows fan out from a single picker:
  //   - admin → "Import vào Drive" (BatchImportModal)
  //   - everyone → "Lưu về máy" (Web Share API → iOS Photos)
  // Capped at BATCH_SELECT_LIMIT cards so Drive batch import doesn't hammer
  // quota and so the share-files fetch fan-out stays manageable.
  const BATCH_SELECT_LIMIT = 15;
  // Hard ceiling on files handed to navigator.share — iOS share sheet
  // starts to choke past ~25 files; keep a comfortable margin.
  const SHARE_FILE_LIMIT = 20;
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [sharing, setSharing] = useState(false);

  // Deep-link: auto-open modal for a specific record when linked from Caption Workstation.
  // Look in allPhotos (unfiltered) so the modal opens even when current filters
  // would otherwise hide the target record.
  useEffect(() => {
    if (!initialRecordId) return;
    const pool = allPhotos && allPhotos.length > 0 ? allPhotos : photos;
    if (pool.length === 0) return;
    const match = pool.find((p) => p.id === initialRecordId);
    if (match) {
      setSelectedPhotoId(initialRecordId);
      onInitialRecordConsumed?.();
    }
  }, [initialRecordId, allPhotos, photos, onInitialRecordConsumed]);

  // Derive selectedPhoto from the live photos array so it updates on optimistic patches
  const selectedPhoto = useMemo(
    () => (selectedPhotoId ? (photos.find((p) => p.id === selectedPhotoId) ?? null) : null),
    [selectedPhotoId, photos]
  );

  const importDrivePhoto = useMemo(
    () => (importDrivePhotoId ? (photos.find((p) => p.id === importDrivePhotoId) ?? null) : null),
    [importDrivePhotoId, photos]
  );

  const resizePhoto = useMemo(
    () => (resizePhotoId ? (photos.find((p) => p.id === resizePhotoId) ?? null) : null),
    [resizePhotoId, photos]
  );

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore,
    hasMore,
    isLoading: isLoadingMore,
    rootMargin: "500px",
  });

  // Keep paging while a client-side filter (month, tags) hides every record on
  // the pages loaded so far. Without this the grid renders its empty state, the
  // scroll sentinel unmounts with it, and the feed can never reach the matches
  // sitting on later pages — a dead end reading "No photos found". Terminates
  // on its own: each page advances the offset until `hasMore` goes false.
  useEffect(() => {
    if (photos.length === 0 && hasMore && !isLoading && !isLoadingMore) onLoadMore();
  }, [photos.length, hasMore, isLoading, isLoadingMore, onLoadMore]);

  const handleCardClick = useCallback((photo: PhotoRecord) => {
    setSelectedPhotoId(photo.id);
  }, []);

  const handleModalClose = useCallback(() => {
    setSelectedPhotoId(null);
  }, []);

  const showError = useCallback((msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(null), 3000);
  }, []);

  const handleToggleLiked = useCallback(
    async (photo: PhotoRecord) => {
      // Per-photographer like: toggle the current user's name in likedBy
      if (currentUser && !isAdmin) {
        const isLiked = photo.likedBy.includes(currentUser);
        const action = isLiked ? "remove" : "add";
        const newLikedBy = isLiked
          ? photo.likedBy.filter((n) => n !== currentUser)
          : [...photo.likedBy, currentUser];
        onUpdatePhoto(photo.id, { likedBy: newLikedBy });
        try {
          await patchPhoto(photo.id, { likedBy: action });
        } catch {
          onUpdatePhoto(photo.id, { likedBy: photo.likedBy });
          showError("Không thể lưu, thử lại nhé.");
        }
      } else {
        // Fallback: global like (admin or no user)
        const newVal = !photo.liked;
        onUpdatePhoto(photo.id, { liked: newVal });
        try {
          await patchPhoto(photo.id, { liked: newVal });
        } catch {
          onUpdatePhoto(photo.id, { liked: photo.liked });
          showError("Could not save liked state.");
        }
      }
    },
    [onUpdatePhoto, showError, currentUser]
  );

  const handleImportDrive = useCallback((photo: PhotoRecord) => {
    setImportDriveQuick(false);
    setImportDrivePhotoId(photo.id);
  }, []);

  const handleQuickImportDrive = useCallback((photo: PhotoRecord) => {
    setImportDriveQuick(true);
    setImportDrivePhotoId(photo.id);
  }, []);

  const handleResize = useCallback((photo: PhotoRecord) => {
    setResizePhotoId(photo.id);
  }, []);

  const handleSaveToCollection = useCallback((photo: PhotoRecord) => {
    setCollectionPhotoId(photo.id);
  }, []);

  const handleQuickTagSave = useCallback(
    async (photo: PhotoRecord, tags: Record<string, string[]>) => {
      // Optimistic update — map field names to PhotoRecord keys
      const fieldToKey: Record<string, string> = {
        kimono_tag: "kimonoTags",
        kimono_color_tag: "kimonoColorTags",
        season_tag: "seasonTags",
        shoot_tag: "shootTags",
        scene_tag: "sceneTags",
      };
      const patch: Partial<PhotoRecord> = {};
      for (const [field, values] of Object.entries(tags)) {
        const key = fieldToKey[field];
        if (key) (patch as Record<string, unknown>)[key] = values;
      }
      onUpdatePhoto(photo.id, patch);

      try {
        await patchPhoto(photo.id, { tags });
      } catch (err) {
        console.error("[quick-tag] save failed:", err);
        setActionError("Lưu tag thất bại");
      }
    },
    [onUpdatePhoto]
  );

  const handleRename = useCallback(
    async (photo: PhotoRecord, newName: string) => {
      // Optimistic update immediately
      onUpdatePhoto(photo.id, { chatName: newName });

      try {
        // Update Airtable (Admin_folder_name field)
        const { patchPhoto } = await import("../lib/api.js");
        await patchPhoto(photo.id, { adminFolderName: newName });
      } catch (err) {
        console.error("[rename] failed:", err);
        setActionError("Đổi tên thất bại");
        // Revert optimistic update
        onUpdatePhoto(photo.id, { chatName: photo.chatName });
      }
    },
    [onUpdatePhoto]
  );

  const handleImportDriveSaved = useCallback(
    (adminDriveLink: string) => {
      if (importDrivePhotoId) {
        onUpdatePhoto(importDrivePhotoId, { adminDriveLink: adminDriveLink || "imported" });
      }
    },
    [importDrivePhotoId, onUpdatePhoto]
  );

  // ── Multi-select handlers ─────────────────────────────────────────────────
  const handleToggleSelectMode = useCallback(() => {
    setSelectMode((on) => {
      if (on) setSelectedIds(new Set());
      return !on;
    });
  }, []);

  const handleToggleSelect = useCallback((photo: PhotoRecord) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(photo.id)) {
        next.delete(photo.id);
      } else {
        if (next.size >= BATCH_SELECT_LIMIT) {
          showError(`Tối đa ${BATCH_SELECT_LIMIT} bộ mỗi lần để tránh quota Drive API.`);
          return prev;
        }
        next.add(photo.id);
      }
      return next;
    });
  }, [showError]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleOpenBatch = useCallback(() => {
    if (selectedIds.size === 0) return;
    setBatchOpen(true);
  }, [selectedIds.size]);

  const handleBatchQueued = useCallback((queuedIds: string[]) => {
    queuedIds.forEach((id) => onUpdatePhoto(id, { adminDriveLink: "imported" }));
  }, [onUpdatePhoto]);

  const handleBatchClose = useCallback(() => {
    setBatchOpen(false);
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  // Bookings included in the batch — derived live from selectedIds + photos
  const selectedPhotos = useMemo(
    () => photos.filter((p) => selectedIds.has(p.id)),
    [photos, selectedIds]
  );

  // Flatten the selected cards' full-quality preview URLs into share items.
  // Each card contributes every R2 `full-N.webp` it has; that's the same
  // image set the lightbox would show, sized for sharing.
  const shareItems = useMemo<ShareItem[]>(() => {
    const items: ShareItem[] = [];
    for (const p of selectedPhotos) {
      const fulls = p.previewURLs?.full ?? [];
      fulls.forEach((url, i) => {
        if (!url) return;
        items.push({ url, baseName: `${p.chatName}-${i + 1}` });
      });
    }
    return items;
  }, [selectedPhotos]);

  const handleShareToDevice = useCallback(async () => {
    if (sharing) return;
    if (shareItems.length === 0) {
      showError("Ảnh chưa sẵn sàng để lưu.");
      return;
    }
    if (shareItems.length > SHARE_FILE_LIMIT) {
      showError(`Tối đa ${SHARE_FILE_LIMIT} ảnh mỗi lần. Đang chọn ${shareItems.length}.`);
      return;
    }
    setSharing(true);
    try {
      const result = await shareImages(shareItems, { title: "KRA Photos" });
      if (result.kind === "shared" || result.kind === "downloaded") {
        setSelectMode(false);
        setSelectedIds(new Set());
      } else if (result.kind === "error") {
        showError(result.message);
      }
      // cancelled → keep selection so user can retry
    } finally {
      setSharing(false);
    }
  }, [sharing, shareItems, showError]);

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4 p-4 max-w-screen-xl mx-auto">
        {Array.from({ length: 12 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-8 text-center">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-status-failed" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v4m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
          </svg>
        </div>
        <p className="text-ink-1 font-medium mb-1">Failed to load photos</p>
        <p className="text-ink-2 text-sm">{error}</p>
      </div>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  // While more pages exist the effect above keeps fetching, so say so rather
  // than claiming there is nothing to find.
  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-8 text-center">
        {hasMore ? (
          <>
            <Spinner />
            <p className="text-ink-2 text-sm mt-4">Đang tìm thêm…</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-surface-2 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-ink-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 9.75h16.5m-16.5 4.5h16.5" />
              </svg>
            </div>
            <p className="text-ink-1 font-medium mb-1">No photos found</p>
            <p className="text-ink-2 text-sm">Try changing the filter</p>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      {/* ── Select-mode toggle (floating top-right, hidden in kiosk) ──── */}
      {!isReadOnly && (
        <div className="sticky top-2 z-30 flex justify-end px-4 max-w-screen-xl mx-auto pointer-events-none">
          <button
            onClick={handleToggleSelectMode}
            className={
              "pointer-events-auto rounded-full px-4 py-2 text-xs font-semibold shadow-card transition-colors " +
              (selectMode
                ? "bg-[#B76E6E] text-white hover:opacity-90"
                : "bg-white text-ink-1 hover:bg-surface-1 border border-surface-2")
            }
          >
            {selectMode ? "Hủy chọn" : "Chọn nhiều"}
          </button>
        </div>
      )}

      {/* ── Grid ──────────────────────────────────────────────────────── */}
      <div className="p-4 max-w-screen-xl mx-auto">
        <AnimatePresence mode="popLayout">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {photos.map((photo, i) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                onClick={handleCardClick}
                index={i}
                onToggleLiked={handleToggleLiked}
                currentUser={currentUser}
                isAdmin={isAdmin}
                isReadOnly={isReadOnly}
                onTagClick={onTagClick}
                onImportDrive={handleImportDrive}
                onQuickImportDrive={handleQuickImportDrive}
                onResize={isAdmin ? handleResize : undefined}
                onRename={isAdmin ? handleRename : undefined}
                tagOptions={isAdmin ? tagOptions : undefined}
                onQuickTagSave={isAdmin ? handleQuickTagSave : undefined}
                onSaveToCollection={isAdmin ? handleSaveToCollection : undefined}
                selectMode={selectMode}
                isSelected={selectedIds.has(photo.id)}
                onToggleSelect={handleToggleSelect}
              />
            ))}
          </div>
        </AnimatePresence>

        {/* ── Load more states ───────────────────────────────────────── */}
        <div ref={sentinelRef} className="py-8 flex justify-center">
          {isLoadingMore && (
            <div className="flex items-center gap-2 text-ink-2 text-sm">
              <Spinner />
              Loading more…
            </div>
          )}
          {!hasMore && photos.length > 0 && (
            <p className="text-ink-3 text-xs">All {photos.length} records loaded</p>
          )}
        </div>
      </div>

      {/* ── Modal ─────────────────────────────────────────────────────── */}
      <PhotoModal
        photo={selectedPhoto}
        onClose={handleModalClose}
        onToggleLiked={handleToggleLiked}
        onUpdatePhoto={onUpdatePhoto}
        currentUser={currentUser}
        isAdmin={isAdmin}
        isReadOnly={isReadOnly}
        allPhotos={allPhotos ?? photos}
        tagOptions={tagOptions}
      />

      {/* ── Import to Drive modal ──────────────────────────────────────── */}
      {importDrivePhoto && (
        <ImportToDriveModal
          photo={importDrivePhoto}
          quick={importDriveQuick}
          onClose={() => setImportDrivePhotoId(null)}
          onSaved={handleImportDriveSaved}
        />
      )}

      {/* ── Resize modal ─────────────────────────────────────────────────── */}
      {resizePhoto && (
        <ResizeModal
          photo={resizePhoto}
          onClose={() => setResizePhotoId(null)}
        />
      )}

      {/* ── Collection picker ──────────────────────────────────────────── */}
      {collectionPhotoId && (
        <CollectionPicker
          photoIds={[collectionPhotoId]}
          onClose={() => setCollectionPhotoId(null)}
        />
      )}

      {/* ── Action error toast ────────────────────────────────────────── */}
      <AnimatePresence>
        {actionError && (
          <ActionErrorToast message={actionError} />
        )}
      </AnimatePresence>

      {/* ── Batch select footer dock ────────────────────────────────────── */}
      <AnimatePresence>
        {selectMode && selectedIds.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-full bg-ink-0/95 px-3 py-2 shadow-2xl backdrop-blur-md max-w-[calc(100vw-1rem)]"
          >
            <span className="px-2 text-sm font-medium text-white whitespace-nowrap">
              {selectedIds.size}/{BATCH_SELECT_LIMIT} bộ
              {shareItems.length > 0 && (
                <span className="ml-1 text-white/60 text-xs">
                  · {shareItems.length} ảnh
                </span>
              )}
            </span>
            <button
              onClick={handleClearSelection}
              disabled={sharing}
              className="rounded-full px-3 py-1.5 text-xs font-medium text-white/80 hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Bỏ chọn
            </button>
            <button
              onClick={handleShareToDevice}
              disabled={sharing || shareItems.length === 0}
              className="flex items-center gap-1.5 rounded-full bg-[#B76E6E] px-4 py-1.5 text-xs font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {sharing ? (
                <>
                  <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" className="animate-spin">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                  </svg>
                  <span>Đang tải…</span>
                </>
              ) : (
                <>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                  <span>Lưu về máy</span>
                </>
              )}
            </button>
            {isAdmin && (
              <button
                onClick={handleOpenBatch}
                disabled={sharing}
                className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/25 transition-colors disabled:opacity-50"
              >
                Drive →
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Batch import modal ─────────────────────────────────────────── */}
      {batchOpen && selectedPhotos.length > 0 && (
        <BatchImportModal
          photos={selectedPhotos}
          onClose={handleBatchClose}
          onQueued={handleBatchQueued}
        />
      )}
    </>
  );
}

// ── Action error toast ─────────────────────────────────────────────────────────

function ActionErrorToast({ message }: { message: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2 }}
      className={
        "fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] " +
        "px-4 py-2 rounded-full bg-ink-0/90 text-surface-0 text-xs font-medium " +
        "shadow-lg backdrop-blur-sm pointer-events-none"
      }
      role="alert"
    >
      {message}
    </motion.div>
  );
}

// ── Skeleton card ─────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-surface-1 rounded-card overflow-hidden animate-pulse">
      <div className="bg-surface-2" style={{ aspectRatio: "3/4" }} />
      <div className="p-3 space-y-2">
        <div className="h-3 bg-surface-2 rounded w-3/4" />
        <div className="h-2.5 bg-surface-2 rounded w-1/2" />
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="w-4 h-4 animate-spin text-ink-2"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"
      />
    </svg>
  );
}
